import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Card, Form, Input, List, Modal } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "utils/axios";
import configFile from "config.json";
import { NotificationManager } from "react-notifications";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const Modifiers = () => {
  const [sizesState, setSizesState] = useState({
    data: [],
    loading: true,
    error: null,
  });
  const [basesState, setBasesState] = useState({
    data: [],
    loading: true,
    error: null,
  });
  const [majorGroupsState, setMajorGroupsState] = useState({
    data: [],
    loading: true,
    error: null,
  });
  const [search, setSearch] = useState({
    sizes: "",
    bases: "",
    majorGroups: "",
  });
  const [isUpsertOpen, setIsUpsertOpen] = useState(false);
  const [upsertTarget, setUpsertTarget] = useState(null);
  const [form] = Form.useForm();

  const sectionMeta = useMemo(
    () => [
      { key: "sizes", label: "Sizes" },
      { key: "bases", label: "Bases" },
      { key: "majorGroups", label: "MajorGroups" },
    ],
    []
  );

  const sectionState = {
    sizes: sizesState,
    bases: basesState,
    majorGroups: majorGroupsState,
  };

  const filteredData = (sectionKey) => {
    const keyword = search[sectionKey].trim().toLowerCase();
    if (!keyword) return sectionState[sectionKey].data;
    return sectionState[sectionKey].data.filter((item) =>
      item?.name?.toLowerCase().includes(keyword)
    );
  };

  let isActive = true;

  const fetchSizes = async () => {
    try {
      const { data: response } = await axios.post(`${API_BASE}/option/listSizes`);
      if (!isActive) return;
      setSizesState({ data: response.data, loading: false, error: null });
    } catch (error) {
      if (!isActive) return;
      setSizesState({ data: [], loading: false, error });
    }
  };

  const fetchBases = async () => {
    try {
      const { data: response } = await axios.post(`${API_BASE}/option/listBases`);
      if (!isActive) return;
      setBasesState({ data: response.data, loading: false, error: null });
    } catch (error) {
      if (!isActive) return;
      setBasesState({ data: [], loading: false, error });
    }
  };

  const fetchMajorGroups = async () => {
    try {
      const { data: response } = await axios.post(`${API_BASE}/option/listMajorGroups`);
      if (!isActive) return;
      setMajorGroupsState({ data: response.data, loading: false, error: null });
    } catch (error) {
      if (!isActive) return;
      setMajorGroupsState({ data: [], loading: false, error });
    }
  };

  useEffect(() => {

    fetchSizes();
    fetchBases();
    fetchMajorGroups();

    return () => {
      isActive = false;
    };
  }, []);

  const openAdd = (sectionKey) => {
    setUpsertTarget({ sectionKey, mode: "add", item: null });
    form.resetFields();
    setIsUpsertOpen(true);
  };

  const openEdit = (sectionKey, item) => {
    setUpsertTarget({ sectionKey, mode: "edit", item });
    form.setFieldsValue({ name: item.name });
    setIsUpsertOpen(true);
  };

  const handleUpsertSave = async () => {
    const values = await form.validateFields();
    if (!upsertTarget) return;
    const isEditMode = upsertTarget.mode === "edit";
    const payload = {
      ...values,
      ...(isEditMode ? { id: upsertTarget.item?.id } : {}),
    };

    try {
      if (upsertTarget.sectionKey === "sizes") {
        const endpoint = isEditMode ? "updateSize" : "createSize";
        await axios.post(`${API_BASE}/option/${endpoint}`, {
          ...payload
        });
        fetchSizes()
      } else if (upsertTarget.sectionKey === "bases") {
        const endpoint = isEditMode ? "updateBase" : "createBase";
        await axios.post(`${API_BASE}/option/${endpoint}`, {
          ...payload
        });
        fetchBases()
      } else if (upsertTarget.sectionKey === "majorGroups") {
        const endpoint = isEditMode ? "updateMajorGroup" : "createMajorGroup";
        await axios.post(`${API_BASE}/option/${endpoint}`, {
          ...payload
        });
        fetchMajorGroups()
      }

      setIsUpsertOpen(false);
      setUpsertTarget(null);
      form.resetFields();
      NotificationManager.success('Амжилттай');
    } catch (err) {
      NotificationManager.error(err.message, 'Error');
      console.log(err)
    }
  };

  return (
    <PageWrap>
      <HeaderCard>
        <div>
          <h1>Modifiers</h1>
          <p>Sizes, Bases, MajorGroups — жагсаалт болон өөрчлөх үйлдлүүд.</p>
        </div>
      </HeaderCard>
      <SectionsGrid>
        {sectionMeta.map((section) => (
          <SectionCard key={section.key}>
            <SectionHeader>
              <div>
                <h3>{section.label}</h3>
                <span>{sectionState[section.key].data.length} items</span>
              </div>
              <HeaderActions>
                <Input
                  placeholder={`Search ${section.label}`}
                  value={search[section.key]}
                  onChange={(event) =>
                    setSearch((prev) => ({
                      ...prev,
                      [section.key]: event.target.value,
                    }))
                  }
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openAdd(section.key)}
                />
              </HeaderActions>
            </SectionHeader>
            <List
              dataSource={filteredData(section.key)}
              locale={{ emptyText: "No items" }}
              loading={sectionState[section.key].loading}
              renderItem={(item) => (
                <RowItem>
                  <div>
                    <ItemTitle>{item.name}</ItemTitle>
                  </div>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(section.key, item)}
                  />
                </RowItem>
              )}
            />
          </SectionCard>
        ))}
      </SectionsGrid>

      <Modal
        open={isUpsertOpen}
        onCancel={() => {
          setIsUpsertOpen(false);
          setUpsertTarget(null);
          form.resetFields();
        }}
        onOk={handleUpsertSave}
        okText={upsertTarget?.mode === "edit" ? "Хадгалах" : "Үүсгэх"}
        cancelText="Цуцлах"
        title={
          upsertTarget?.mode === "edit"
            ? `${sectionMeta.find((s) => s.key === upsertTarget.sectionKey)?.label || ""} засварлах`
            : `${sectionMeta.find((s) => s.key === upsertTarget?.sectionKey)?.label || ""} үүсгэх`
        }
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrap>
  );
};

const PageWrap = styled.div`
  padding: 24px;
  min-height: 100vh;
  background: radial-gradient(circle at top, #e0f2fe 0%, #f8fafc 45%, #ffffff 100%);
  font-family: "Poppins", "Segoe UI", sans-serif;
`;

const HeaderCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid rgba(22, 119, 255, 0.18);
  box-shadow: 0 18px 50px rgba(33, 49, 63, 0.1);
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);

  .ant-card-body {
    padding: 18px 22px;
  }

  h1 {
    margin: 0 0 6px 0;
    font-size: 22px;
    color: #1f2a33;
  }

  p {
    margin: 0;
    color: #6b7a88;
  }
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const SectionCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  max-height: 520px;
  overflow: hidden;

  .ant-card-body {
    padding: 0;
    max-height: 520px;
    display: flex;
    flex-direction: column;
  }

  .ant-list {
    overflow-y: auto;
    max-height: 420px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(96, 165, 250, 0.2);
  background: #eff6ff;

  h3 {
    margin: 0 0 4px 0;
    font-size: 15px;
    color: #1d4ed8;
  }

  span {
    font-size: 12px;
    color: #64748b;
  }

  .ant-btn {
    border-radius: 5px;
  }

  .ant-btn-primary {
    color: #ffffff;
    background: #2563eb;
    border-color: #2563eb;
  }

  .ant-btn-primary:hover,
  .ant-btn-primary:focus {
    color: #ffffff;
    background: #1d4ed8;
    border-color: #1d4ed8;
  }

  .ant-btn-primary .anticon svg {
    color: #ffffff;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .ant-input {
    width: 160px;
    border-radius: 5px;
    border-color: rgba(96, 165, 250, 0.5);
  }
`;

const RowItem = styled(List.Item)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px !important;
  margin: 0 !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);

  .ant-btn {
    color: #2563eb;
  }
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
`;

export default Modifiers;
